import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { AlertListOptions } from "@shared-core/data/adapter";
import type { Alert } from "@shared-core/data/api-types";
import { AlertItem } from "../app/components/domain/alerts/AlertItem";
import {
  EntityCombobox,
  type EntityOption,
} from "../app/components/patterns/EntityCombobox";
import { paths } from "@shared-core/routing/routes";
import { CustomerScopeChip } from "../app/components/domain/customers/CustomerScopeChip";

// AlertsPage follows the canonical list-endpoint pattern (see
// docs/list-endpoints.md): the backend does filter/sort/paginate.
// The page owns filter state and refetches on change; there is no
// client-side `.filter()` or `.sort()` on the adapter response.

type StatusTab = "open" | "dismissed";
type SeverityFilter = "all" | "critical" | "high" | "medium" | "low";

const PAGE_SIZE = 100;

// The "Type" typeahead offers both granularities: pick a family (broad
// grouping) or a specific alert name. The selected option encodes which via
// an ``family:`` / ``name:`` id prefix, mapped to the relevant filter param.
function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function AlertsPage() {
  const dataAdapter = useDataAdapter();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerFilter = searchParams.get("customer") ?? undefined;
  const typeName = searchParams.get("name") ?? undefined;
  const typeFamily = searchParams.get("family") ?? undefined;

  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusTab>("open");
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("all");
  // Remember the customer option picked in the typeahead so the trigger shows
  // the name (the URL only carries the id). Falls back to the row-derived name
  // for deep links / refresh.
  const [pickedCustomer, setPickedCustomer] = useState<EntityOption | null>(
    null,
  );

  const buildOptions = useCallback((): AlertListOptions => {
    const options: AlertListOptions = {
      status: statusFilter,
      sort: "severity",
      pageSize: PAGE_SIZE,
    };
    if (severityFilter !== "all") options.severity = severityFilter;
    if (customerFilter) options.customerId = customerFilter;
    if (typeName) options.alertName = typeName;
    if (typeFamily) options.alertFamily = typeFamily;
    return options;
  }, [statusFilter, severityFilter, customerFilter, typeName, typeFamily]);

  // Refetch alerts whenever filter state changes. Sorting "severity"
  // puts critical-first (whitelisted on the backend).
  useEffect(() => {
    let cancelled = false;
    async function loadAlerts() {
      setLoading(true);
      try {
        const response = await dataAdapter.getAlerts(buildOptions());
        if (!cancelled) setAlerts(response.items);
      } catch (error) {
        console.error("Error loading alerts:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadAlerts();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, buildOptions]);

  const refreshAlerts = async () => {
    const response = await dataAdapter.getAlerts(buildOptions());
    setAlerts(response.items);
  };

  // Typeahead option sources. Type options are the live alert-definition
  // catalog (families + specific names); customer options come from the
  // customer search endpoint.
  const fetchTypeOptions = useCallback(
    async (q: string): Promise<EntityOption[]> => {
      const res = await dataAdapter.getAlertDefinitions();
      const live = res.items.filter((d) => d.status === "live");
      const families = Array.from(new Set(live.map((d) => d.family))).sort();
      const opts: EntityOption[] = [
        ...families.map((f) => ({
          id: `family:${f}`,
          label: titleCase(f),
          hint: "Family",
        })),
        ...live
          .map((d) => ({ id: `name:${d.name}`, label: d.name, hint: "Type" }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
      const needle = q.trim().toLowerCase();
      return needle
        ? opts.filter((o) => o.label.toLowerCase().includes(needle))
        : opts;
    },
    [dataAdapter],
  );

  const fetchCustomerOptions = useCallback(
    async (q: string): Promise<EntityOption[]> => {
      const res = await dataAdapter.getCustomers({
        q: q || undefined,
        pageSize: 20,
      });
      return res.items.map((c) => ({ id: c.id, label: c.name }));
    },
    [dataAdapter],
  );

  const severities: { id: SeverityFilter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "critical", label: "Critical" },
    { id: "high", label: "High" },
    { id: "medium", label: "Medium" },
    { id: "low", label: "Low" },
  ];

  // When filtering by customer, every row carries that customer in
  // ``alert.customer`` — pull the name from the first row rather than
  // a second round-trip.
  const customerName = customerFilter
    ? (alerts.find((a) => a.customer_id === customerFilter)?.customer?.name ??
      null)
    : null;

  const selectedType: EntityOption | null = typeName
    ? { id: `name:${typeName}`, label: typeName, hint: "Type" }
    : typeFamily
      ? {
          id: `family:${typeFamily}`,
          label: titleCase(typeFamily),
          hint: "Family",
        }
      : null;

  const selectedCustomer: EntityOption | null = customerFilter
    ? pickedCustomer && pickedCustomer.id === customerFilter
      ? pickedCustomer
      : { id: customerFilter, label: customerName ?? customerFilter }
    : null;

  const handleTypeChange = (opt: EntityOption | null) => {
    const next = new URLSearchParams(searchParams);
    next.delete("name");
    next.delete("family");
    if (opt) {
      const sep = opt.id.indexOf(":");
      const kind = opt.id.slice(0, sep);
      const value = opt.id.slice(sep + 1);
      next.set(kind === "family" ? "family" : "name", value);
    }
    setSearchParams(next, { replace: true });
  };

  const handleCustomerChange = (opt: EntityOption | null) => {
    setPickedCustomer(opt);
    const next = new URLSearchParams(searchParams);
    if (opt) next.set("customer", opt.id);
    else next.delete("customer");
    setSearchParams(next, { replace: true });
  };

  const handleDismissCustomerScope = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("customer");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl mb-2">
          {customerFilter ? `Alerts for ${customerName ?? "…"}` : "Alerts"}
        </h1>
        {customerFilter && (
          <div className="mb-4">
            <CustomerScopeChip
              customerName={customerName}
              onDismiss={handleDismissCustomerScope}
            />
          </div>
        )}

        {/* Filters */}
        <div className="space-y-4">
          {/* Search by customer (top-left, mirroring estimates / orders) */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-full sm:w-72">
              <EntityCombobox
                value={selectedCustomer}
                onChange={handleCustomerChange}
                fetchOptions={fetchCustomerOptions}
                placeholder="Search by customer…"
                ariaLabel="Search by customer"
              />
            </div>
          </div>

          {/* Status Tabs */}
          <div className="flex gap-2 border-b border-border">
            <button
              onClick={() => setStatusFilter("open")}
              className={`
                px-4 py-2 border-b-2 transition-colors
                ${
                  statusFilter === "open"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }
              `}
            >
              Open
            </button>
            <button
              onClick={() => setStatusFilter("dismissed")}
              className={`
                px-4 py-2 border-b-2 transition-colors
                ${
                  statusFilter === "dismissed"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }
              `}
            >
              Dismissed
            </button>
          </div>

          {/* Severity pills (left) + Type filter (right) */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex gap-2 flex-wrap">
              {severities.map((severity) => (
                <button
                  key={severity.id}
                  onClick={() => setSeverityFilter(severity.id)}
                  className={`
                    px-4 py-2 rounded-full text-sm transition-colors
                    ${
                      severityFilter === severity.id
                        ? "bg-primary text-white"
                        : "bg-surface border border-border text-foreground hover:bg-surface-hover"
                    }
                  `}
                >
                  {severity.label}
                </button>
              ))}
            </div>
            <div className="w-full sm:w-64 shrink-0">
              <EntityCombobox
                value={selectedType}
                onChange={handleTypeChange}
                fetchOptions={fetchTypeOptions}
                placeholder="Filter by type…"
                ariaLabel="Filter by alert type"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="text-sm text-muted-foreground">
        {loading ? "Loading alerts…" : `Showing ${alerts.length} alerts`}
      </div>

      {/* Alerts List */}
      {!loading && alerts.length === 0 ? (
        <div className="text-sm p-8 text-center border border-border rounded-lg space-y-1">
          <p className="font-medium text-foreground">
            {statusFilter === "open"
              ? "No active alerts"
              : "No dismissed alerts"}
          </p>
          {statusFilter === "open" && (
            <p className="text-muted-foreground">
              You'll see alerts here when a customer's pattern shifts.{" "}
              <Link
                to={paths.settingsAlerts()}
                className="text-primary hover:underline"
              >
                Set up what to watch for in Settings → Alerts.
              </Link>
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {alerts.map((alert) => (
            <AlertItem
              key={alert.id}
              alert={alert}
              onDismiss={async () => {
                await dataAdapter.updateAlert(alert.id, {
                  status: "dismissed",
                });
                await refreshAlerts();
              }}
              onConvertToTicket={async () => {
                // assigned_to_user_id omitted — the backend assigns to the creator.
                const newTicket = await dataAdapter.createTicket({
                  title: alert.title,
                  customer_id: alert.customer_id,
                  alert_id: alert.id,
                  action_type: "general",
                  due_date: undefined,
                });
                await dataAdapter.updateAlert(alert.id, {
                  status: "converted_to_task",
                  converted_to_task_id: newTicket.id,
                });
                await refreshAlerts();
              }}
              onViewTicket={() => navigate(paths.tickets())}
            />
          ))}
        </div>
      )}
    </div>
  );
}
